import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { LanguageService } from '../../../../services/i18n/language';
import { DictionaryService, CountryDto } from '../../../../services/api/dictionary';
import { CustomsCalculation, UpdateShipmentInfoRequest, FreightBreakdownItem } from '../../../../services/api/customs-calculations';
import { CalcSection } from '../calculation-sections';
import { CountrySelectComponent } from '../../country-select/country-select';

/** Временный флаг — ссылка/модалка «Обоснование» AI-оценки доставки скрыта на
 *  фронте по просьбе пользователя, пока данные ещё проверяются. Бэкенд продолжает
 *  считать и сохранять freightJustification как обычно. */
const DELIVERY_JUSTIFICATION_UI_ENABLED = false;

export interface ShipmentFormValue {
  fromCountryId: number | null;
  fromCity: string;
  toCountryId: number | null;
  toCity: string;
  totalWeightKg: number | null;
  totalVolumeM3: number | null;
  transportType: 'FTL' | 'LTL' | 'AIR' | 'CONTAINER';
}

@Component({
  selector: 'app-calculation-summary-panel',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, RouterModule, CountrySelectComponent],
  templateUrl: './calculation-summary-panel.html',
  styleUrl: './calculation-summary-panel.scss'
})
export class CalculationSummaryPanelComponent implements OnInit, OnChanges, OnDestroy {
  /** 'pending' — расчёт ещё обрабатывается (создание/загрузка инвойса), показываем скелетон итогов.
   *  'loaded' — расчёт уже создан, показываем реальные данные из calculation. */
  @Input() mode: 'pending' | 'loaded' = 'loaded';
  @Input() calculation: CustomsCalculation | null = null;
  @Input() detailsOpen = false;
  @Input() detailsAvailable = true;
  /** Раздел кабинета клиента: показываем только его часть панели; null — вся панель, как раньше. */
  @Input() section: CalcSection | null = null;
  /** Только просмотр (админ смотрит расчёт клиента): форма закрыта, действия скрыты. */
  @Input() viewOnly = false;
  /** Родитель выставляет true на время запроса сохранения — блокирует кнопку и меняет её подпись. */
  @Input() saving = false;
  /** Родитель выставляет true на время AI-оценки стоимости доставки. */
  @Input() estimatingDelivery = false;
  @Input() deliveryEstimateError: string | null = null;

  /** Эмитится только по явному нажатию кнопки «Сохранить» — форму можно менять и «примерять»
   *  варианты сколько угодно без побочных эффектов, пока пользователь сам не подтвердит сохранение. */
  @Output() shipmentInfoChange = new EventEmitter<UpdateShipmentInfoRequest>();
  @Output() toggleDetails = new EventEmitter<void>();
  @Output() startCertificatesCheck = new EventEmitter<void>();
  @Output() estimateDeliveryCostRequested = new EventEmitter<UpdateShipmentInfoRequest>();

  shipmentForm: FormGroup;
  countries: CountryDto[] = [];
  readonly transportTypeOptions: Array<{ value: 'FTL' | 'LTL' | 'AIR' | 'CONTAINER'; label: string }> = [
    { value: 'FTL', label: 'FTL (Выделенный АТС)' },
    { value: 'LTL', label: 'LTL (Сборный груз)' },
    { value: 'AIR', label: 'Авиа' },
    { value: 'CONTAINER', label: 'Контейнер' }
  ];
  /** Есть несохранённые изменения формы поставки (для loaded-режима) */
  isDirty = false;

  // Translations
  shipmentInfoTitleText = 'Информация о поставке';
  fromCountryLabelText = 'Страна отправления';
  fromCityLabelText = 'Город отправления';
  toCountryLabelText = 'Страна назначения';
  toCityLabelText = 'Город назначения';
  countryNotSpecifiedText = 'Не указана';
  cityPlaceholderText = 'Город';
  totalWeightLabelText = 'Вес груза, кг';
  totalVolumeLabelText = 'Объём груза, м³';
  transportTypeLabelText = 'Тип перевозки';
  deliveryCostSectionTitleText = 'Стоимость доставки';
  deliveryCostHintText = 'Платформа оценит общую стоимость доставки по маршруту, весу и объёму груза.';

  // ─── Ручной ввод стоимости доставки (кабинет клиента) ─────────────────────
  manualOpen = false;
  manualAmount: number | null = null;
  manualCurrency = 'USD';
  manualError: string | null = null;
  readonly manualCurrencies = ['USD', 'EUR', 'CNY', 'RUB', 'KZT'];

  /**
   * Стоимость введена клиентом, а не посчитана ИИ. На сервере такого признака пока нет —
   * храним в браузере по id расчёта (как ответ «доставит ли продавец»).
   */
  get isManualFreight(): boolean {
    const id = this.calculation?.id;
    return !!id && this.hasDeliveryEstimate && !!readManualFreight()[id];
  }

  openManualFreight(): void {
    this.manualError = null;
    this.manualAmount = this.isManualFreight ? Number(this.calculation?.freightAmount ?? 0) || null : null;
    this.manualCurrency = (this.isManualFreight && this.calculation?.freightCurrencyCode) || 'USD';
    this.manualOpen = true;
  }

  /** Сохраняем сумму как доставку до границы — она входит в таможенную стоимость, платежи пересчитываются. */
  applyManualFreight(): void {
    const amount = Number(this.manualAmount);
    if (!this.manualAmount || !(amount > 0)) {
      this.manualError = this.st('calculationSummary.manualFreightInvalid', 'Укажите сумму больше нуля');
      return;
    }
    if (this.calculation?.id) {
      writeManualFreight(this.calculation.id, true);
    }
    this.shipmentInfoChange.emit({
      ...this.buildRequest(),
      freightEnabled: true,
      freightAmount: Math.round(amount * 100) / 100,
      freightAfterBorderAmount: 0,
      freightCurrencyCode: this.manualCurrency
    });
    this.manualOpen = false;
    this.manualError = null;
  }

  /** Суммы в тенге: в кабинете — по языку и без копеек («784 259»), у брокера — как раньше. */
  money(value: number | null | undefined): string {
    const v = Number(value ?? 0);
    if (this.section) {
      const lang = this.languageService.getCurrentLanguageValue();
      const locale = ({ ru: 'ru-RU', kk: 'kk-KZ', en: 'en-US', zh: 'zh-CN' } as Record<string, string>)[lang] ?? 'ru-RU';
      return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(Math.round(v));
    }
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(v);
  }

  /** Перевод с запасным текстом, пока ключа нет в словаре. */
  st(key: string, fallback: string): string {
    const value = this.languageService.translate(key);
    return value && value !== key ? value : fallback;
  }
  estimateDeliveryButtonText = 'Рассчитать стоимость доставки';
  estimatingDeliveryText = 'Считаем…';
  estimateDeliveryRequiresSaveText = 'Сначала укажите страну отправления, страну назначения, вес, объём и тип перевозки';
  deliveryResultTitleText = 'Стоимость доставки (рассчитано ИИ)';
  deliveryToBorderLabelText = 'Стоимость доставки до границы';
  deliveryToAirportLabelText = 'Стоимость доставки до аэропорта';
  deliveryAfterBorderLabelText = 'Стоимость доставки после границы';
  deliveryAfterAirportLabelText = 'Стоимость доставки после аэропорта';
  includedInCustomsValueText = 'учитывается при расчёте таможенной стоимости (80%, при авиа — 100%)';
  notIncludedInCustomsValueText = 'не включается в таможенную стоимость: доставку оплачивает продавец';
  deliveryTotalLabelText = 'Общая стоимость доставки';
  informationalOnlyText = 'только для информации, не влияет на таможенную стоимость';
  deliveryJustificationLinkText = 'Обоснование';
  deliveryJustificationModalTitleText = 'Из чего складывается стоимость доставки';
  deliveryJustificationNoAmountText = 'сумма не оценена отдельно';
  unsavedHintText = 'Есть несохранённые изменения';
  savedHintText = 'Сохранено';
  saveButtonText = 'Сохранить';
  savingButtonText = 'Сохранение…';
  resultsTitleText = 'Результаты расчёта';
  calculatingBadgeText = 'Идёт расчёт…';
  preliminaryBadgeText = 'Предварительный расчёт';
  totalLabelText = 'Итого к уплате в бюджет';
  restrictionsClearText = 'Товары не подпадают под запреты и ограничения';
  restrictionsRestrictedText = 'Есть товары, требующие разрешительных документов';
  restrictionsUnknownText = 'Проверка ограничений доступна в детальном расчёте';
  groupRouteText = 'Маршрут';
  groupCargoText = 'Груз';
  restrictionsAnalysisPendingText = 'Анализ разрешительных документов';
  restrictionsRunCheckText = 'Расчёт';
  restrictionsCheckingText = 'Идёт проверка…';
  restrictionsMoreDetailsText = 'Подробнее';
  restrictionsRefreshTitleText = 'Пересчитать разрешительные документы';
  restrictionsModalTitleText = 'Разрешительные документы по кодам ТНВЭД';
  restrictionsModalRequiredText = 'требуются разрешительные документы';
  restrictionsModalNotRequiredText = 'разрешительные документы не требуются';
  exciseNoticeText = 'В расчёте есть товары (алкоголь/табак), для которых акциз не рассчитывается — см. пометку у товара в детальном расчёте.';
  paymentTypeHeaderText = 'Вид платежа';
  amountHeaderText = 'Сумма, ₸';
  totalRowText = 'Итого';
  customsFeesText = 'Таможенный сбор';
  customsDutyText = 'Таможенная пошлина';
  vatText = 'НДС';
  exciseText = 'Акциз';
  antiDumpingText = 'Антидемпинговая пошлина';
  exclusionsSummaryText = 'Для каких товаров мы не рассчитываем акциз';
  exclusion1Text = 'Алкогольная продукция — ТН ВЭД 2203–2208 (пиво, вино, крепкие спиртные напитки)';
  exclusion2Text = 'Табак и никотиносодержащая продукция — ТН ВЭД глава 24 (сигареты, табак, вейпы)';
  exclusionsNoteText = 'Пошлина, НДС и сбор по этим товарам считаются как обычно.';
  showDetailsButtonText = 'Открыть детальный расчёт';
  hideDetailsButtonText = 'Скрыть детальный расчёт';

  private subscriptions = new Subscription();
  private suppressEmit = false;

  constructor(
    private fb: FormBuilder,
    private languageService: LanguageService,
    private dictionaryService: DictionaryService,
    private cdr: ChangeDetectorRef
  ) {
    this.shipmentForm = this.fb.group({
      fromCountryId: [null],
      fromCity: [''],
      toCountryId: [null],
      toCity: [''],
      totalWeightKg: [null],
      totalVolumeM3: [null],
      transportType: ['FTL']
    });
  }

  ngOnInit(): void {
    this.updateTranslations();
    this.subscriptions.add(
      this.languageService.getTranslations().subscribe(() => {
        this.updateTranslations();
        this.cdr.markForCheck();
      })
    );

    this.subscriptions.add(
      this.dictionaryService.getAllCountries().subscribe({
        next: (countries) => { this.countries = countries; this.cdr.markForCheck(); },
        error: (err) => console.error('Error loading countries:', err)
      })
    );

    this.subscriptions.add(
      this.shipmentForm.valueChanges.subscribe(() => {
        // Форма — это «черновик», который можно менять свободно (пользователь примеряет
        // варианты стоимости доставки). На бэкенд ничего не уходит, пока не нажата «Сохранить».
        if (this.suppressEmit) return;
        this.isDirty = true;
        this.cdr.markForCheck();
      })
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['viewOnly']) {
      if (this.viewOnly) {
        this.shipmentForm.disable({ emitEvent: false });
      } else {
        this.shipmentForm.enable({ emitEvent: false });
      }
    }
    if (!changes['calculation'] || !this.calculation) return;

    // Сюда прилетают и обновления calculation от СВОЕГО сохранения/оценки доставки (тогда
    // saving/estimatingDelivery только что переключились true → false в этом же цикле — их
    // обязательно нужно подхватить и сбросить isDirty), и обновления от ДРУГИХ действий на
    // странице — например, смена Incoterms в блоке инвойса (см. onInvoiceIncotermChange) или
    // живое обновление через сокет. Для вторых, если в форме есть несохранённые правки,
    // перетирать их нельзя — иначе пользователь молча теряет то, что уже ввёл, но не сохранил.
    const ownUpdateJustCompleted =
      changes['saving']?.previousValue === true ||
      changes['estimatingDelivery']?.previousValue === true;
    if (ownUpdateJustCompleted || !this.isDirty) {
      this.patchFromCalculation(this.calculation);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private updateTranslations(): void {
    this.shipmentInfoTitleText = this.languageService.translate('calculationSummary.shipmentInfoTitle') || this.shipmentInfoTitleText;
    this.fromCountryLabelText = this.languageService.translate('calculationSummary.fromCountryLabel') || this.fromCountryLabelText;
    this.fromCityLabelText = this.languageService.translate('calculationSummary.fromCityLabel') || this.fromCityLabelText;
    this.toCountryLabelText = this.languageService.translate('calculationSummary.toCountryLabel') || this.toCountryLabelText;
    this.toCityLabelText = this.languageService.translate('calculationSummary.toCityLabel') || this.toCityLabelText;
    this.countryNotSpecifiedText = this.languageService.translate('calculationSummary.countryNotSpecified') || this.countryNotSpecifiedText;
    this.cityPlaceholderText = this.languageService.translate('calculationSummary.cityPlaceholder') || this.cityPlaceholderText;
    this.totalWeightLabelText = this.languageService.translate('calculationSummary.totalWeightLabel') || this.totalWeightLabelText;
    this.totalVolumeLabelText = this.languageService.translate('calculationSummary.totalVolumeLabel') || this.totalVolumeLabelText;
    this.transportTypeLabelText = this.languageService.translate('calculationSummary.transportTypeLabel') || this.transportTypeLabelText;
    this.deliveryCostSectionTitleText = this.languageService.translate('calculationSummary.deliveryCostSectionTitle') || this.deliveryCostSectionTitleText;
    this.deliveryCostHintText = this.languageService.translate('calculationSummary.deliveryCostHint') || this.deliveryCostHintText;
    this.estimateDeliveryButtonText = this.languageService.translate('calculationSummary.estimateDeliveryButton') || this.estimateDeliveryButtonText;
    this.estimatingDeliveryText = this.languageService.translate('calculationSummary.estimatingDelivery') || this.estimatingDeliveryText;
    this.estimateDeliveryRequiresSaveText = this.languageService.translate('calculationSummary.estimateDeliveryRequiresSave') || this.estimateDeliveryRequiresSaveText;
    this.deliveryResultTitleText = this.languageService.translate('calculationSummary.deliveryResultTitle') || this.deliveryResultTitleText;
    this.deliveryToBorderLabelText = this.languageService.translate('calculationSummary.deliveryToBorderLabel') || this.deliveryToBorderLabelText;
    this.deliveryToAirportLabelText = this.languageService.translate('calculationSummary.deliveryToAirportLabel') || this.deliveryToAirportLabelText;
    this.deliveryAfterBorderLabelText = this.languageService.translate('calculationSummary.deliveryAfterBorderLabel') || this.deliveryAfterBorderLabelText;
    this.deliveryAfterAirportLabelText = this.languageService.translate('calculationSummary.deliveryAfterAirportLabel') || this.deliveryAfterAirportLabelText;
    this.includedInCustomsValueText = this.languageService.translate('calculationSummary.includedInCustomsValue') || this.includedInCustomsValueText;
    this.notIncludedInCustomsValueText = this.languageService.translate('calculationSummary.notIncludedInCustomsValue') || this.notIncludedInCustomsValueText;
    this.deliveryTotalLabelText = this.languageService.translate('calculationSummary.deliveryTotalLabel') || this.deliveryTotalLabelText;
    this.informationalOnlyText = this.languageService.translate('calculationSummary.informationalOnly') || this.informationalOnlyText;
    this.deliveryJustificationLinkText = this.languageService.translate('calculationSummary.deliveryJustificationLink') || this.deliveryJustificationLinkText;
    this.deliveryJustificationModalTitleText = this.languageService.translate('calculationSummary.deliveryJustificationModalTitle') || this.deliveryJustificationModalTitleText;
    this.deliveryJustificationNoAmountText = this.languageService.translate('calculationSummary.deliveryJustificationNoAmount') || this.deliveryJustificationNoAmountText;
    this.transportTypeOptions[0].label = this.languageService.translate('cargoOwner.createRequest.rateTypeFTL') || this.transportTypeOptions[0].label;
    this.transportTypeOptions[1].label = this.languageService.translate('cargoOwner.createRequest.rateTypeLTL') || this.transportTypeOptions[1].label;
    this.transportTypeOptions[2].label = this.languageService.translate('cargoOwner.createRequest.rateTypeAIR') || this.transportTypeOptions[2].label;
    this.transportTypeOptions[3].label = this.languageService.translate('cargoOwner.createRequest.rateTypeCONTAINER') || this.transportTypeOptions[3].label;
    this.unsavedHintText = this.languageService.translate('calculationSummary.unsavedHint') || this.unsavedHintText;
    this.savedHintText = this.languageService.translate('calculationSummary.savedHint') || this.savedHintText;
    this.saveButtonText = this.languageService.translate('calculationSummary.saveButton') || this.saveButtonText;
    this.savingButtonText = this.languageService.translate('calculationSummary.savingButton') || this.savingButtonText;
    this.resultsTitleText = this.languageService.translate('calculationSummary.resultsTitle') || this.resultsTitleText;
    this.calculatingBadgeText = this.languageService.translate('calculationSummary.calculatingBadge') || this.calculatingBadgeText;
    this.preliminaryBadgeText = this.languageService.translate('calculationSummary.preliminaryBadge') || this.preliminaryBadgeText;
    this.totalLabelText = this.languageService.translate('calculationSummary.totalLabel') || this.totalLabelText;
    this.restrictionsClearText = this.languageService.translate('calculationSummary.restrictionsClear') || this.restrictionsClearText;
    this.restrictionsRestrictedText = this.languageService.translate('calculationSummary.restrictionsRestricted') || this.restrictionsRestrictedText;
    this.restrictionsUnknownText = this.languageService.translate('calculationSummary.restrictionsUnknown') || this.restrictionsUnknownText;
    this.restrictionsAnalysisPendingText = this.languageService.translate('calculationSummary.restrictionsAnalysisPending') || this.restrictionsAnalysisPendingText;
    this.groupRouteText = this.languageService.translate('calculationSummary.groupRoute') || this.groupRouteText;
    this.groupCargoText = this.languageService.translate('calculationSummary.groupCargo') || this.groupCargoText;
    this.restrictionsRunCheckText = this.languageService.translate('calculationSummary.restrictionsRunCheck') || this.restrictionsRunCheckText;
    this.restrictionsCheckingText = this.languageService.translate('calculationSummary.restrictionsChecking') || this.restrictionsCheckingText;
    this.restrictionsMoreDetailsText = this.languageService.translate('calculationSummary.restrictionsMoreDetails') || this.restrictionsMoreDetailsText;
    this.restrictionsRefreshTitleText = this.languageService.translate('calculationSummary.restrictionsRefreshTitle') || this.restrictionsRefreshTitleText;
    this.restrictionsModalTitleText = this.languageService.translate('calculationSummary.restrictionsModalTitle') || this.restrictionsModalTitleText;
    this.restrictionsModalRequiredText = this.languageService.translate('calculationSummary.restrictionsModalRequired') || this.restrictionsModalRequiredText;
    this.restrictionsModalNotRequiredText = this.languageService.translate('calculationSummary.restrictionsModalNotRequired') || this.restrictionsModalNotRequiredText;
    this.exciseNoticeText = this.languageService.translate('calculationSummary.exciseNotice') || this.exciseNoticeText;
    this.paymentTypeHeaderText = this.languageService.translate('calculationSummary.paymentTypeHeader') || this.paymentTypeHeaderText;
    this.amountHeaderText = this.languageService.translate('calculationSummary.amountHeader') || this.amountHeaderText;
    this.totalRowText = this.languageService.translate('calculationSummary.totalRow') || this.totalRowText;
    this.customsFeesText = this.languageService.translate('calculationSummary.customsFees') || this.customsFeesText;
    this.customsDutyText = this.languageService.translate('calculationSummary.customsDuty') || this.customsDutyText;
    this.vatText = this.languageService.translate('calculationSummary.vat') || this.vatText;
    this.exciseText = this.languageService.translate('calculationSummary.excise') || this.exciseText;
    this.antiDumpingText = this.languageService.translate('calculationSummary.antiDumping') || this.antiDumpingText;
    this.exclusionsSummaryText = this.languageService.translate('calculationSummary.exclusionsSummary') || this.exclusionsSummaryText;
    this.exclusion1Text = this.languageService.translate('calculationSummary.exclusion1') || this.exclusion1Text;
    this.exclusion2Text = this.languageService.translate('calculationSummary.exclusion2') || this.exclusion2Text;
    this.exclusionsNoteText = this.languageService.translate('calculationSummary.exclusionsNote') || this.exclusionsNoteText;
    this.showDetailsButtonText = this.languageService.translate('calculationSummary.showDetailsButton') || this.showDetailsButtonText;
    this.hideDetailsButtonText = this.languageService.translate('calculationSummary.hideDetailsButton') || this.hideDetailsButtonText;
  }

  private patchFromCalculation(calc: CustomsCalculation): void {
    this.suppressEmit = true;
    this.shipmentForm.patchValue({
      fromCountryId: calc.fromCountryId ?? null,
      fromCity: calc.fromCity ?? '',
      toCountryId: calc.toCountryId ?? null,
      toCity: calc.toCity ?? '',
      totalWeightKg: calc.totalWeightKg ?? null,
      totalVolumeM3: calc.totalVolumeM3 ?? null,
      transportType: calc.transportType ?? 'FTL'
    }, { emitEvent: false });
    this.suppressEmit = false;
    this.isDirty = false;
  }

  /** Явное сохранение данных о поставке — вызывается только по клику на кнопку. */
  onSaveShipmentInfo(): void {
    this.shipmentInfoChange.emit(this.buildRequest());
  }

  private buildRequest(): UpdateShipmentInfoRequest {
    const v = this.shipmentForm.value;
    return {
      fromCountryId: v.fromCountryId ?? undefined,
      fromCity: v.fromCity || undefined,
      toCountryId: v.toCountryId ?? undefined,
      toCity: v.toCity || undefined,
      // Incoterms редактируются в блоке товаров/инвойса — здесь просто передаём текущее
      // сохранённое значение, чтобы не затереть его этим (несвязанным) сохранением.
      incotermCode: this.calculation?.incotermCode ?? undefined,
      totalWeightKg: v.totalWeightKg ?? undefined,
      totalVolumeM3: v.totalVolumeM3 ?? undefined,
      transportType: v.transportType || undefined,
      // Стоимость доставки считает только ИИ (кнопка ниже) — здесь просто передаём то,
      // что уже посчитано и сохранено, чтобы не затереть его этим (несвязанным) сохранением.
      freightEnabled: this.calculation?.freightEnabled ?? undefined,
      freightAmount: this.calculation?.freightAmount ?? undefined,
      freightAfterBorderAmount: this.calculation?.freightAfterBorderAmount ?? undefined,
      freightCurrencyCode: this.calculation?.freightCurrencyCode ?? undefined
    };
  }

  /** Текущее значение формы поставки — используется родителем в pending-режиме перед созданием расчёта. */
  getShipmentValue(): UpdateShipmentInfoRequest {
    return this.buildRequest();
  }

  onToggleDetails(): void {
    this.toggleDetails.emit();
  }

  // ─── Стоимость доставки ────────────────────────────────────────────────────

  get isAirTransport(): boolean {
    return this.shipmentForm.value.transportType === 'AIR';
  }

  get deliveryBeforePointLabel(): string {
    return this.isAirTransport ? this.deliveryToAirportLabelText : this.deliveryToBorderLabelText;
  }

  get deliveryAfterPointLabel(): string {
    return this.isAirTransport ? this.deliveryAfterAirportLabelText : this.deliveryAfterBorderLabelText;
  }

  /** Доставка входит в таможенную стоимость, только если товар доставляет не продавец (ответ «Нет»):
   *  80% стоимости доставки, при авиа 100%. Пока ответа нет — не входит. Incoterms не учитывается. */
  get deliveryCountsTowardsCustomsValue(): boolean {
    return this.calculation?.sellerDelivers === false;
  }

  get customsValueNoteText(): string {
    return this.deliveryCountsTowardsCustomsValue ? this.includedInCustomsValueText : this.notIncludedInCustomsValueText;
  }

  /** Кнопка сама сохраняет черновик формы перед AI-оценкой — поэтому смотрим именно
   *  на текущие (возможно ещё не сохранённые) значения формы, а не на calculation. */
  get canEstimateDelivery(): boolean {
    const v = this.shipmentForm.value;
    return !!v.fromCountryId && !!v.toCountryId
      && v.totalWeightKg != null && v.totalWeightKg !== ''
      && v.totalVolumeM3 != null && v.totalVolumeM3 !== ''
      && !!v.transportType;
  }

  /** Стоимость доставки считает только ИИ — ручного ввода нет. Кнопка сначала сохраняет
   *  текущий черновик формы (маршрут/вес/объём/тип), затем запускает AI-оценку. */
  onEstimateDeliveryCost(): void {
    if (!this.canEstimateDelivery) return;
    // Новая оценка ИИ заменяет введённую вручную сумму
    if (this.calculation?.id) {
      writeManualFreight(this.calculation.id, false);
    }
    this.manualOpen = false;
    this.estimateDeliveryCostRequested.emit(this.buildRequest());
  }

  /** Одна сумма за всю доставку: до границы (аэропорта) + после. При авиа второй части нет. */
  get deliveryTotalAmount(): number {
    const before = Number(this.calculation?.freightAmount ?? 0);
    const after = this.isAirTransport ? 0 : Number(this.calculation?.freightAfterBorderAmount ?? 0);
    return before + after;
  }

  get hasDeliveryEstimate(): boolean {
    return this.calculation?.freightAmount != null || this.calculation?.freightAfterBorderAmount != null;
  }

  // ─── Модалка «Обоснование» AI-оценки стоимости доставки ───────────────────

  get hasDeliveryJustification(): boolean {
    // Временно скрыто на фронте по просьбе пользователя — данные по-прежнему считаются
    // и сохраняются на бэкенде, просто ссылка/модалка пока не показываются.
    if (!DELIVERY_JUSTIFICATION_UI_ENABLED) return false;
    const justification = this.calculation?.freightJustification;
    return !!justification && (!!justification.notes || (justification.breakdown?.length ?? 0) > 0);
  }

  showDeliveryJustificationModal = false;

  onShowDeliveryJustificationModal(): void {
    this.showDeliveryJustificationModal = true;
  }

  closeDeliveryJustificationModal(): void {
    this.showDeliveryJustificationModal = false;
  }

  get deliveryJustificationNotes(): string | null {
    return this.calculation?.freightJustification?.notes ?? null;
  }

  get deliveryJustificationBreakdown(): FreightBreakdownItem[] {
    return this.calculation?.freightJustification?.breakdown ?? [];
  }

  // ─── Результаты (агрегаты) ────────────────────────────────────────────────

  get isPending(): boolean {
    return this.mode === 'pending';
  }

  get totalPayments(): number | null {
    return this.calculation?.totalCustomsPayments ?? null;
  }

  /** Уникальные коды ТНВЭД уже классифицированных товаров — именно по ним идёт проверка документов. */
  private get classifiedTnvedCodes(): string[] {
    const codes = (this.calculation?.products || [])
      .map(p => p.tnvedCodeForBroker)
      .filter((code): code is string => !!code);
    return Array.from(new Set(codes));
  }

  get certificatesProcessing(): boolean {
    return this.calculation?.certificatesProcessingStatus === 'PROCESSING';
  }

  /** Проверка запускалась и завершилась по ВСЕМ текущим кодам (иначе — считаем, что нужен (пере)запуск). */
  private get certificatesFullyResolved(): boolean {
    const codes = this.classifiedTnvedCodes;
    if (codes.length === 0) return false;
    const checklists = this.calculation?.certificateChecklists || [];
    return codes.every(code => checklists.some(c => c.tnvedCode === code));
  }

  get restrictionsStatus(): 'restricted' | 'clear' | 'unknown' | 'processing' | 'not-checked' {
    if (!this.calculation || this.mode === 'pending') return 'unknown';
    if (!this.calculation.allProductsClassified) return 'unknown';
    if (this.certificatesProcessing) return 'processing';
    if (!this.certificatesFullyResolved) return 'not-checked';
    const restricted = (this.calculation.certificateChecklists || [])
      .some(c => c.requiredDocuments && c.requiredDocuments.length > 0);
    return restricted ? 'restricted' : 'clear';
  }

  onRunCertificatesCheck(): void {
    this.startCertificatesCheck.emit();
  }

  // ─── Модалка «Подробнее» по разрешительным документам ─────────────────────

  showRestrictionsModal = false;

  onShowRestrictionsModal(): void {
    this.showRestrictionsModal = true;
  }

  closeRestrictionsModal(): void {
    this.showRestrictionsModal = false;
  }

  get restrictionsModalRows(): { code: string; hasDocuments: boolean }[] {
    const checklists = this.calculation?.certificateChecklists || [];
    return this.classifiedTnvedCodes.map(code => {
      const checklist = checklists.find(c => c.tnvedCode === code);
      const hasDocuments = !!checklist?.requiredDocuments && checklist.requiredDocuments.length > 0;
      return { code, hasDocuments };
    });
  }

  /** Доля платежа в общей сумме, % (для полосы под строкой). */
  share(value: number | null): number {
    const total = this.totalPayments;
    if (!value || !total || total <= 0) return 0;
    return Math.max(2, Math.round((value / total) * 100));
  }

  get paymentRows(): { label: string; value: number | null }[] {
    const c = this.calculation;
    return [
      { label: this.customsFeesText, value: c?.totalCustomsFees ?? null },
      { label: this.customsDutyText, value: c?.totalCustomsDuty ?? null },
      { label: this.vatText, value: c?.totalVat ?? null },
      { label: this.exciseText, value: c?.totalExcise ?? null },
      { label: this.antiDumpingText, value: c?.totalAntiDumpingDuty ?? null }
    ];
  }
}

const MANUAL_FREIGHT_KEY = 'bilload.calc.freightManual';

function readManualFreight(): Record<number, boolean> {
  try {
    return JSON.parse(localStorage.getItem(MANUAL_FREIGHT_KEY) || '{}');
  } catch {
    return {};
  }
}

function writeManualFreight(id: number, manual: boolean): void {
  try {
    const map = readManualFreight();
    if (manual) {
      map[id] = true;
    } else {
      delete map[id];
    }
    localStorage.setItem(MANUAL_FREIGHT_KEY, JSON.stringify(map));
  } catch {
    // localStorage недоступен — подпись останется «прогноз ИИ», сумма всё равно сохранена
  }
}
